import { request } from "http";
import { OPERATION_TYPE, OperationType, CreateBidType, QuoteAcceptData, BidEntity, BidListResponse } from "interfaces";
import { api } from "./api";
import { TRequestValues } from "./authClient/auth/api-utils";
import { ENDPOINTS } from "./endpoints";
import { operations, paths } from "../types/openapi";
import { getBidOperationEndpoint } from "utils";

export type getAllOperation = operations["getAll_1"] & BidListResponse;
type getByIdOperation = operations["getById"];
type cancelOperation = operations["cancel"];
type executeOperation = operations["execute"];
type stepBackOperation = operations["stepBack"];
type refundOperation = operations["refund"];
type forceAmlPassedOperation = operations["forceAmlPassed"];
type approveOperation = operations["approve"];
type updateFeeOperation = operations["updateFee"];
type agentProposalOperation = operations["agentProposal"];
type acceptAgentProposalOperation = operations["acceptAgentProposal"];
type updateBankAccountOperation = operations["updateBankAccount"];
// type speedUpByBidIdOperation = operations["speedUpByBidId"];
type getPendingActionsOperation = operations["getPendingActions"];
type getPageByContractIdOperation = operations["getPageByContractId"];
type getHistoryOperation = operations["getHistory"];
type declineDraftOperation = operations["declineDraft"];
type returnRequestToDraftOperation = operations["returnRequestToDraft"];
type accountDetailsOperation = operations["accountDetails"];
type updateBalanceOperation = operations["balance"];
type getRefundWalletsOperation = operations["getRefundWallets"];
type refundNativeOperation = operations["refundNative"];
type amlHoldOperation = operations["amlHold"];
type approveTransactionOperation = operations["approveTransaction"];
type getWalletCheckGridOperation = operations["getWalletChecks"];
type getTransactionListOperation = operations["getAll_5"];
type approveWalletCheckOperation = operations["walletCheckApprove"];
type getAmlCheckStatusOperation = paths["/aml-check-status/{requestId}"]["get"];
type requestNominalFundsWithdrawalOperation = operations["updateFee"];
type requestCustomerExecutedOperation = operations["executeByCustomer"];
type updateCurrencyOperationCodeOperation = operations["updateCurrencyOperationCode"];

/**
 * Грид заявок (доступен для CUSTOMER, OPERATOR, LIQUIDITY_PROVIDER)
 */
export const getBidList = (params: Record<string, TRequestValues>): Promise<BidListResponse> => {
  return api.get(ENDPOINTS.bids(), params);
};

/**
 * Грид заявок с ожидающими действиями (доступен для CUSTOMER, OPERATOR, LIQUIDITY_PROVIDER)
 */
export const getPendingActionsBidList = (
  params: Record<string, TRequestValues>
): Promise<getPendingActionsOperation["responses"]["200"]["content"]["*/*"]> => {
  return api.get(ENDPOINTS.pendingActionsBids(), params);
};

/**
 * Грид заявок для договора ID (CUSTOMER, OPERATOR)
 */
export const getContractBidList = (
  { id }: getPageByContractIdOperation["parameters"]["path"],
  params: getPageByContractIdOperation["parameters"]["query"]
): Promise<getPageByContractIdOperation["responses"]["200"]["content"]["*/*"]> => {
  return api.get(ENDPOINTS.contractBids(id), params);
};

/**
 * Карточка заявки (доступен для CUSTOMER, OPERATOR, LIQUIDITY_PROVIDER)
 */
export const getBidById = ({ id }: getByIdOperation["parameters"]["path"]): Promise<BidEntity> => {
  return api.get(ENDPOINTS.bidById(id));
};

/**
 * Создать новую заявку (доступен для CUSTOMER)
 */
export const createBid = (formData: CreateBidType): Promise<string> => {
  return api.post(ENDPOINTS.bids(), formData, {
    contentType: "multipart/form-data",
  });
};

/**
 * Отменяет заявку (доступен для CUSTOMER, OPERATOR)
 */
export const cancelBid = ({
  id,
}: cancelOperation["parameters"]["path"]): Promise<cancelOperation["responses"]["200"]["content"]> => {
  return api.post(ENDPOINTS.cancelBid(id));
};

/**
 * Отклоняет черновик заявки (доступен для OPERATOR)
 */
export const declineDraftBid = (
  { id }: declineDraftOperation["parameters"]["path"],
  formData: declineDraftOperation["requestBody"]["content"]["application/json"]
): Promise<declineDraftOperation["responses"]["200"]["content"]> => {
  return api.post(ENDPOINTS.declineDraftBid(id), formData);
};

/**
 * Возвращает отклоненный черновик заявки на рассмотрение (доступен для CUSTOMER)
 */
export const returnBidToDraft = ({
  id,
}: returnRequestToDraftOperation["parameters"]["path"]): Promise<
  returnRequestToDraftOperation["responses"]["200"]["content"]
> => {
  return api.post(ENDPOINTS.returnBidToDraft(id));
};

export type RelevantOperationType = Exclude<OperationType, "LOGIN" | "PASSWORD" | "REQUEST_CREATE" | "QUOTE_CREATE">;

export const BID_OPERATION_PATH: Record<RelevantOperationType, string> = {
  [OPERATION_TYPE.BANK_TRANSFER_SENT]: "fiat-sent",
  [OPERATION_TYPE.REQUEST_APPROVE]: "accept-quote",
  [OPERATION_TYPE.BANK_TRANSFER_RECEIVED]: "fiat-received",
  [OPERATION_TYPE.BANK_TRANSFER_RECEIVED_BY_PLATFORM]: "fiat-received-by-platform",
  [OPERATION_TYPE.BANK_TRANSFER_WITHDRAWN]: "fiat-withdrawn",
  [OPERATION_TYPE.RUB_WITHDRAWAL_REQUEST]: "rub-withdrawal-request",
  [OPERATION_TYPE.CUSTOMER_EXECUTION]: "customer-execute",
  [OPERATION_TYPE.VERIFY_WALLET_REQUEST]: "verify-destination-wallet",
  [OPERATION_TYPE.VERIFY_WALLET_QUOTE]: "verify-wallet",
  [OPERATION_TYPE.NOMINAL_ACCOUNT_DEPOSIT]: "rub-operations/deposit",
};

export const createBidOperation = (
  params: {
    id: string;
    operationType: RelevantOperationType;
    quoteRequestData?: QuoteAcceptData;
  },
  formData: string
): Promise<string> => {
  return api.post(getBidOperationEndpoint(params), formData, {
    contentType: "multipart/form-data",
  });
};

/**
 * Банковские реквизиты для котировки (LIQUIDITY_PROVIDER, OPERATOR, CUSTOMER)
 */
export const getBankStatement = ({
  id,
}: accountDetailsOperation["parameters"]["path"]): Promise<
  accountDetailsOperation["responses"]["200"]["content"]["*/*"]
> => {
  return api.get(ENDPOINTS.bankStatement(id));
};

/**
 * Обновляет значение комиссии платформы для заявки (доступен для OPERATOR)
 */
export const editCompanyFeeForBid = (
  { id }: updateFeeOperation["parameters"]["path"],
  formData: updateFeeOperation["requestBody"]["content"]["application/json"]
): Promise<updateFeeOperation["responses"]["200"]["content"]> => {
  return api.post(ENDPOINTS.editCompanyFeeForBid(id), formData, {
    contentType: "multipart/form-data",
  });
};

/**
 * Ускорение исходящей транзакции (доступен для OPERATOR)
 */
// export const speedUpBidTransaction = ({
//   id,
// }: speedUpByBidIdOperation["parameters"]["path"]): Promise<speedUpByBidIdOperation["responses"]["200"]["content"]> => {
//   return api.post(ENDPOINTS.speedUpBidTransaction(id));
// };

/**
 * Принудительно переводит заявку из статуса AML_FAILED (доступен для OPERATOR)
 */
export const passAmlCheck = ({
  id,
}: forceAmlPassedOperation["parameters"]["path"]): Promise<forceAmlPassedOperation["responses"]["200"]["content"]> => {
  return api.post(ENDPOINTS.passAmlCheck(id));
};

/**
 * Возвращает средства (доступен для OPERATOR)
 */
export const refundCurrency = (
  { id }: refundOperation["parameters"]["path"],
  address: string | null
): Promise<refundOperation["responses"]["200"]["content"]> => {
  return api.post(ENDPOINTS.refundCurrency(id), { address });
};

/**
 * Исполнить заявку, переводит в статус EXECUTION (доступен для OPERATOR)
 */
export const executeBid = ({
  id,
}: executeOperation["parameters"]["path"]): Promise<executeOperation["responses"]["200"]["content"]> => {
  return api.post(ENDPOINTS.executeBid(id));
};

/**
 * Возвращает заявку на 1 статус назад (доступен для CUSTOMER, OPERATOR)
 */
export const stepBack = ({
  id,
}: stepBackOperation["parameters"]["path"]): Promise<stepBackOperation["responses"]["200"]["content"]> => {
  return api.post(ENDPOINTS.stepBack(id));
};

/**
 * Переводит заявку в статус AGENT_PROPOSAL_ACCEPTED (доступен для CUSTOMER)
 */
export const acceptBidConditions = ({
  id,
}: acceptAgentProposalOperation["parameters"]["path"]): Promise<
  acceptAgentProposalOperation["responses"]["200"]["content"]
> => {
  return api.post(ENDPOINTS.acceptBidConditions(id));
};

/**
 * Переводит заявку в статус NEW (доступен для OPERATOR)
 */
export const approveBid = ({
  id,
}: approveOperation["parameters"]["path"]): Promise<approveOperation["responses"]["200"]["content"]> => {
  return api.post(ENDPOINTS.approveBid(id));
};

/**
 * Переводит заявку в статус AGENT_PROPOSAL (доступен для OPERATOR)
 */
export const agentProposal = (
  { id }: agentProposalOperation["parameters"]["path"],
  formData: agentProposalOperation["requestBody"]["content"]["application/json"]
): Promise<agentProposalOperation["responses"]["200"]["content"]> => {
  return api.post(ENDPOINTS.agentProposal(id), formData, {
    contentType: "multipart/form-data",
  });
};

/**
 * История заявки (доступен для CUSTOMER, OPERATOR, LIQUIDITY_PROVIDER)
 */
export const getBidStatusHistory = ({
  id,
}: getHistoryOperation["parameters"]["path"]): Promise<getHistoryOperation["responses"]["200"]["content"]["*/*"]> => {
  return api.get(ENDPOINTS.bidHistory(id));
};

/**
 * Обновляет банковский счет (доступен для CUSTOMER, OPERATOR, LIQUIDITY_PROVIDER)
 */
export const changeBidBankAccount = (
  { id }: updateBankAccountOperation["parameters"]["path"],
  accountId: updateBankAccountOperation["requestBody"]["content"]["application/json"]
): Promise<updateBankAccountOperation["responses"]["200"]["content"]> => {
  return api.put(ENDPOINTS.bidBankAccount(id), JSON.stringify(accountId));
};

/**
 * Возвращает статус по вызову операции обновления и скачивания (доступен для CUSTOMER, OPERATOR, LIQUIDITY_PROVIDER)
 */
export const getBidBalance = ({
  id,
}: updateBalanceOperation["parameters"]["path"]): Promise<updateBalanceOperation["responses"]["200"]["content"]> => {
  return api.get(ENDPOINTS.getBidBalance(id));
};

/**
 * Возвращает статус по вызову операции обновления и скачивания (доступен для CUSTOMER, OPERATOR, LIQUIDITY_PROVIDER)
 */
export const getRefundWallets = ({
  id,
}: getRefundWalletsOperation["parameters"]["path"]): Promise<
  getRefundWalletsOperation["responses"]["200"]["content"]["*/*"]
> => {
  return api.get(ENDPOINTS.getRefundWallets(id));
};

/**
 * Грид транзакций
 * @description Грид транзакций (доступен для SENIOR_CUSTOMER, JUNIOR_CUSTOMER, SENIOR_LP, JUNIOR_LP, SENIOR_OPERATOR, JUNIOR_OPERATOR, RISK_MANAGER)
 */
export const getBidTransactionList = (
  { requestId }: getTransactionListOperation["parameters"]["path"],
  params: Record<string, TRequestValues>
): Promise<any> => {
  return api.get(ENDPOINTS.bidTransactions(requestId), params);
};

/**
 * Переводит заявку в статус AML_FAILED
 * @description Переводит заявку в статус AML_FAILED (доступен для RISK_MANAGER)
 */
export const stopDeal = ({
  id,
}: amlHoldOperation["parameters"]["path"]): Promise<amlHoldOperation["responses"]["200"]["content"]> => {
  return api.post(ENDPOINTS.stopDeal(id));
};

/**
 * Одобрить отдельную транзакцию
 * @description Одобрить отдельную транзакцию (RISK_MANAGER)
 */
export const approveTransaction = ({
  hash,
}: approveTransactionOperation["parameters"]["path"]): Promise<
  approveTransactionOperation["responses"]["200"]["content"]
> => {
  return api.post(ENDPOINTS.approveTransaction(hash));
};

/**
 * Одобрить отдельный кошелек
 * @description Одобрить отдельный кошелек (RISK_MANAGER)
 */
export const approveWalletCheck = ({
  checkId,
}: approveWalletCheckOperation["parameters"]["path"]): Promise<
  approveWalletCheckOperation["responses"]["200"]["content"]
> => {
  return api.post(ENDPOINTS.approveWalletCheck(checkId));
};

export const getRefundNative = ({
  id,
}: refundNativeOperation["parameters"]["path"]): Promise<refundNativeOperation["responses"]["200"]["content"]> => {
  return api.post(ENDPOINTS.getRefundNative(id));
};

/**
 * Список проверок кошельков для заявки
 * @description Список проверок кошельков для заявки (доступен для RISK_MANAGER)
 */
export const getBidWalletCheckList = (
  { id }: getWalletCheckGridOperation["parameters"]["path"],
  params: Record<string, TRequestValues>
): Promise<getWalletCheckGridOperation["responses"]["200"]["content"]["*/*"]> => {
  return api.get(ENDPOINTS.bidWalletChecks(id), params);
};

/**
 *  Возвращает список статусных проверок aml (доступно для: SENIOR_OPERATOR, JUNIOR_OPERATOR, RISK_MANAGER)
 */
export const getAmlCheckStatus = ({
  requestId,
}: getAmlCheckStatusOperation["parameters"]["path"]): Promise<
  getAmlCheckStatusOperation["responses"]["200"]["content"]["*/*"]
> => {
  return api.get(ENDPOINTS.amlCheckStatus(requestId));
};

/**
 * Пополнение заявки с номинального счёта (доступен для PROVIDER)
 */
export const requestNominalFundsWithdrawal = (
  { id }: requestNominalFundsWithdrawalOperation["parameters"]["path"],
  formData: requestNominalFundsWithdrawalOperation["requestBody"]["content"]["application/json"]
): Promise<requestNominalFundsWithdrawalOperation["responses"]["200"]["content"]> => {
  return api.post(ENDPOINTS.requestNominalFundsWithdrawal(id), formData, {
    contentType: "application/json",
  });
};

/**
 * Проставляет флаг customer_executed
 */
export const requestCustomerExecuted = ({
  id,
}: requestCustomerExecutedOperation["parameters"]["path"]): Promise<
  requestCustomerExecutedOperation["responses"]["200"]["content"]
> => {
  return api.post(ENDPOINTS.requestCustomerExecuted(id));
};

export const updateCurrencyOperationCode = (
  { id }: updateCurrencyOperationCodeOperation["parameters"]["path"],
  formData: updateCurrencyOperationCodeOperation["requestBody"]["content"]["application/json"]
): Promise<updateCurrencyOperationCodeOperation["responses"]["200"]["content"]> => {
  return api.post(ENDPOINTS.updateCurrencyOperationCode(id), formData, {
    contentType: "application/json",
  });
};
